import urlBuilder from '../../utils/urlBuilder';

const mapCompanySummaryToSelectTable = (companySummary, translation) => {
    const dataCols = [
        {
            id: 'company', numeric: false, disablePadding: false, label: translation.COMPANY, defaultSorting: false,
        },
        {
            id: 'city', numeric: false, disablePadding: false, label: translation.CITY, defaultSorting: false,
        },
        {
            id: 'hid', numeric: false, disablePadding: false, label: translation.HID, defaultSorting: false,
        },
        {
            id: 'orderId', numeric: false, disablePadding: false, label: translation.ACTIVE_ORDER, defaultSorting: false, action: true,
        },
        {
            id: 'lead', numeric: true, disablePadding: false, label: translation.LEADS, defaultSorting: true,
        },
        {
            id: 'impression', numeric: true, disablePadding: false, label: translation.IMPRESSIONS, defaultSorting: true,
        },
        {
            id: 'link', numeric: false, disablePadding: false, label: translation.LINK, defaultSorting: false, disableSorting: true, link: true,
        },
    ];

    const dataRows = companySummary.map(company => ({
        id: company.hid,
        company: company.name,
        city: company.city,
        hid: company.hid,
        orderId: company.orderId > 0 ? company.orderId.toString() : '',
        lead: company.lead,
        impression: company.impression,
        link: urlBuilder.getHittaCompanyUrl(company.hid),
    }));

    return {
        dataCols,
        dataRows,
    };
};

const mapCompanySummaryToCompanyTable = (companySummary, translation) => {
    const dataCols = [
        {
            id: 'company', numeric: false, disablePadding: false, label: translation.COMPANY, defaultSorting: false,
        },
        {
            id: 'city', numeric: false, disablePadding: false, label: translation.CITY, defaultSorting: false,
        },
        {
            id: 'lead', numeric: true, disablePadding: false, label: translation.LEADS, defaultSorting: true,
        },
        {
            id: 'impression', numeric: true, disablePadding: false, label: translation.IMPRESSIONS, defaultSorting: false,
        },
    ];

    const dataRows = companySummary.map(company => ({
        id: company.hid,
        company: company.name,
        city: company.city,
        lead: company.lead,
        impression: company.impression,
    }));

    return {
        dataCols,
        dataRows,
    };
};

const calculateShare = (count, totalCount) => {
    const share = Math.round(
        count * 10000
        / totalCount,
    ) / 100;

    return Number.isNaN(share) ? '-' : `${share}%`;
};

const mapProductSummaryToLeadTable = (
    productSummary,
    translation,
    totalLeadCount,
    notGroupedProducts,
) => {
    const grouping = notGroupedProducts
        .reduce((result, notGrouped) => (
            { ...result, [notGrouped]: 0 }
        ), { other: 0 });

    const reduction = productSummary.reduce((result, summary) => {
        if (Object.prototype.hasOwnProperty.call(result, summary.name)) {
            return { ...result, [summary.name]: summary.lead };
        }
        return { ...result, other: result.other + summary.lead };
    }, grouping);

    const zerosRemoved = Object.keys(reduction)
        .reduce((result, key) => (
            { ...result, ...(reduction[key] > 0 && { [key]: reduction[key] }) }
        ), {});

    const dataCols = [
        {
            id: 'type', numeric: false, disablePadding: false, label: translation.CONTACT, defaultSorting: false,
        },
        {
            id: 'count', numeric: true, disablePadding: false, label: translation.COUNT, defaultSorting: true,
        },
        {
            id: 'share', numeric: true, disablePadding: false, label: translation.SHARE, defaultSorting: false,
        },
    ];

    const dataRows = Object.keys(zerosRemoved).map(key => ({
        id: key,
        type: translation.PRODUCT_KEYS[key] || translation.OTHER,
        tooltip: translation.TOOLTIP_PRODUCT_LEAD[key],
        count: reduction[key],
        share: calculateShare(reduction[key], totalLeadCount),
    }));

    return {
        dataCols,
        dataRows,
    };
};

const mapImpressionTable = (
    productSummary,
    totalDetailImpressionCount,
    totalListImpressionCount,
    totalImpressionCount,
    translation,
) => {
    const dataCols = [
        {
            id: 'type', numeric: false, disablePadding: false, label: translation.IMPRESSION, defaultSorting: false,
        },
        {
            id: 'count', numeric: true, disablePadding: false, label: translation.COUNT, defaultSorting: true,
        },
        {
            id: 'share', numeric: true, disablePadding: false, label: translation.SHARE, defaultSorting: false,
        },
    ];

    const suggestProductCount = (productSummary.find(product => product.name === 'suggest') || { impression: 0 }).impression;
    const mapProductCount = (productSummary.find(product => product.name === 'kartuppgift') || { impression: 0 }).impression;

    const totalCount = totalDetailImpressionCount
    + totalListImpressionCount
    + suggestProductCount
    + mapProductCount;

    const totalCountString = totalCount.toLocaleString('sv-se');

    const rows = [
        {
            id: 'DETAIL_EXPOSURE',
            type: translation.PAGE_KEYS.detail,
            tooltip: translation.TOOLTIP_DETAIL_EXPOSURE,
            count: totalDetailImpressionCount,
            share: calculateShare(totalDetailImpressionCount, totalCount),
        },
        {
            id: 'LIST_EXPOSURE',
            type: translation.PAGE_KEYS.list,
            tooltip: translation.TOOLTIP_LIST_EXPOSURE,
            count: totalListImpressionCount,
            share: calculateShare(totalListImpressionCount, totalCount),
        },
        {
            id: 'SUGGEST_EXPOSURE',
            type: translation.PRODUCT_KEYS.suggest,
            tooltip: translation.TOOLTIP_SUGGEST_EXPOSURE,
            count: suggestProductCount,
            share: calculateShare(suggestProductCount, totalCount),
        },
        {
            id: 'MAP_EXPOSURE',
            type: translation.PAGE_KEYS.map,
            tooltip: translation.TOOLTIP_MAP_EXPOSURE,
            count: mapProductCount,
            share: calculateShare(mapProductCount, totalCount),
        },
    ];

    const dataRows = rows.filter(row => row.count > 0);

    return {
        totalCount,
        totalCountString,
        dataCols,
        dataRows,
    };
};

export {
    mapCompanySummaryToSelectTable,
    mapProductSummaryToLeadTable,
    mapCompanySummaryToCompanyTable,
    mapImpressionTable,
};
